import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import type { OperationTypeFindInterface } from '@zaoblako/shared/modules/document/dtos/operation-type/operation-type-find.dto';
import type { OperationTypeFindPageQueryInterface } from '@zaoblako/shared/modules/document/dtos/operation-type/operation-type-find-page-query.dto';
import { OperationTypeService } from '@zaoblako/web/core/services/finance/operation-type.service';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';

@Component({
  selector: 'crm-operation-type-single-select',
  standalone: true,
  imports: [CommonModule, SingleSelectComponent, SpinnerComponent],
  templateUrl: './operation-type-single-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OperationTypeSingleSelectComponent implements OnInit {
  @ViewChild('singleSelectComponent', { static: true }) public singleSelectComponent: SingleSelectComponent;

  @Input() public operationTypes: OperationTypeFindInterface[] = [];
  public queryParams: OperationTypeFindPageQueryInterface = {
    limit: 40,
    offset: 0
  };

  public loading = false;
  public paginationLoading = false;
  public count = 0;

  public _selectedOperationType: OperationTypeFindInterface;
  @Input() public set selectedOperationType(selectedOperationType: OperationTypeFindInterface) {
    if (this.customSearch) {
      this._selectedOperationType = selectedOperationType;
      return;
    }

    if (this.operationTypes.some((operationType) => operationType.id === selectedOperationType?.id)) {
      this._selectedOperationType = this.operationTypes.find(
        (operationType) => operationType.id === selectedOperationType.id
      );
      return;
    }

    if (selectedOperationType?.id) {
      if (typeof selectedOperationType.id === 'string') {
        selectedOperationType.id = +selectedOperationType.id;
      }

      this.operationTypeService.findById<OperationTypeFindInterface>(selectedOperationType.id).subscribe({
        next: (_operationType) => {
          if (!this.operationTypes.some((operationType) => operationType.id === _operationType.id)) {
            this.operationTypes = this.operationTypes.concat(_operationType);
            this.cdRef.markForCheck();
          }
          setTimeout(() => {
            this._selectedOperationType = _operationType;
            this.cdRef.markForCheck();
          }, 30);
        }
      });
    }
  }

  @Output() public selectedOperationTypeChange = new EventEmitter<OperationTypeFindInterface>();

  @Input() public customSearch = false;

  @Input() public class: string;
  @Input() public styleClass: string;
  @Input() public showClear = true;
  @Input() public disabled = false;
  @Input() public isError = false;
  @Input() public appendTo: string;
  @Input() public labelInput = false;

  @Input() public set setSelectedOperationTypeId(id: number) {
    if (id === null || id === undefined) {
      return;
    }

    let _id = id;
    if (typeof id === 'string') {
      _id = +id;
    }

    if (this.operationTypes.some((operationType) => operationType.id === _id)) {
      this._selectedOperationType = this.operationTypes.find((operationType) => operationType.id === _id);
      return;
    }

    this.operationTypeService.findById<OperationTypeFindInterface>(_id).subscribe({
      next: (_operationType) => {
        if (!this.operationTypes.some((operationType) => operationType.id === _operationType.id)) {
          this.operationTypes = this.operationTypes.concat(_operationType);
          this.cdRef.markForCheck();
        }
        setTimeout(() => {
          this._selectedOperationType = _operationType;
          this.cdRef.markForCheck();
        }, 30);
      }
    });
  }

  public constructor(
    private readonly operationTypeService: OperationTypeService,
    private readonly cdRef: ChangeDetectorRef
  ) {}

  public ngOnInit(): void {
    if (!this.customSearch) {
      this.loading = true;
      this.cdRef.markForCheck();

      this.operationTypeService.findWithPagination(this.queryParams).subscribe({
        next: (data) => {
          this.operationTypes = this.operationTypes?.length
            ? this.operationTypes.concat(
                data.items.filter((item) => !this.operationTypes.some((operationType) => operationType.id === item.id))
              )
            : data.items.filter((item) => !this.operationTypes.some((operationType) => operationType.id === item.id));
          this.count = data.count;
          this.loading = false;
          this.cdRef.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.cdRef.markForCheck();
        }
      });
    }
  }

  public onSelect(operationType: OperationTypeFindInterface) {
    this.selectedOperationTypeChange.emit(operationType);
  }

  public onShowPanel() {
    if (!this.customSearch) {
      setTimeout(() => {
        this.singleSelectComponent?.dropdown?.scroller.onScroll.subscribe(() => {
          const el: HTMLElement = this.singleSelectComponent?.dropdown?.scroller?.elementViewChild?.nativeElement;
          if (el.offsetHeight + Math.ceil(el.scrollTop) >= el.scrollHeight) {
            if (this.operationTypes.length < this.count && !this.loading && !this.paginationLoading) {
              this.paginationLoading = true;
              this.queryParams.offset += this.queryParams.limit;
              this.cdRef.markForCheck();

              this.operationTypeService.findWithPagination(this.queryParams).subscribe({
                next: (data) => {
                  this.operationTypes = this.operationTypes.concat(
                    data.items.filter(
                      (item) => !this.operationTypes.some((operationType) => operationType.id === item.id)
                    )
                  );
                  this.count = data.count;
                  this.paginationLoading = false;
                  this.cdRef.markForCheck();
                },
                error: () => {
                  this.paginationLoading = false;
                  this.cdRef.markForCheck();
                }
              });
            }
          }
        });
      }, 50);
    }
  }

  public search(name: string) {
    if (!this.customSearch) {
      this.loading = true;
      this.queryParams.search = name;
      this.queryParams.offset = 0;
      this.cdRef.markForCheck();

      this.operationTypeService.findWithPagination(this.queryParams).subscribe({
        next: (data) => {
          if (!data.items.some((item) => item.id === this._selectedOperationType?.id) && this._selectedOperationType) {
            this.operationTypes = data.items.concat(this._selectedOperationType);
          } else {
            this.operationTypes = data.items;
          }
          this.count = data.count;
          this.loading = false;
          this.cdRef.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.cdRef.markForCheck();
        }
      });
    }
  }
}
